/**
 * คำสั่งอนุมัติของ Product Master — `submit` / `approve` / `reject`
 *
 * แถบนี้คือครึ่งที่ "กดได้" ของ D-010 ส่วนครึ่งที่ "อ่านได้" คือฟิลด์ผู้ดำเนินการ
 * บนหน้ารายละเอียด สามคำสั่งนี้คือทั้งหมดที่ contract freeze ไว้ — **ไม่มี**
 * การแก้ไข (PATCH เป็นหน้าจอแยก) คำขอเปลี่ยนแปลง หรือการปิดใช้งาน เพราะสอง
 * อย่างหลังยังไม่มี endpoint ให้เรียก
 *
 * กติกาสามข้อที่ทั้งไฟล์นี้ยืนอยู่บน:
 *
 * 1. **`expected_status` คือสิ่งที่ถูกเรนเดอร์ ไม่ใช่สิ่งที่โค้ดคิดว่าควรเป็น**
 *    ค่าที่ส่งไปถูกอ่านจาก `product.status` ก้อนเดียวกับที่ผู้ใช้กำลังมองอยู่
 *    ตอนกดปุ่ม ถ้าไปหยิบค่าล่าสุดมาใส่ให้เอง guard จะเหลือแค่การยืนยันว่า
 *    ข้อมูลที่เพิ่งโหลดตรงกับข้อมูลที่เพิ่งโหลด ซึ่งไม่กันอะไรเลย
 * 2. **`409` ไม่ลองใหม่ให้อัตโนมัติ** มันแปลว่ามีคนอื่นขยับ record นี้ไปแล้ว
 *    ระหว่างที่หน้าจอเปิดค้าง สิ่งที่ทำคือดึงของจริงมาแสดง **หนึ่งครั้ง** แล้วให้
 *    คนเป็นผู้ตัดสินใหม่ การยิงซ้ำบน record ที่เปลี่ยนไปแล้วคืออุบัติเหตุที่ guard
 *    นี้มีไว้ป้องกันพอดี
 * 3. **ความล้มเหลวห้ามขยับสถานะบนจอ** สถานะที่แสดงจะเปลี่ยนก็ต่อเมื่อเซิร์ฟเวอร์
 *    ส่ง resource ใหม่กลับมาเท่านั้น ไม่มีการเดาผลล่วงหน้าแบบ optimistic
 *
 * ⚠️ การเลือกแสดง/ไม่แสดงปุ่มที่นี่เป็น **ความสะดวก ไม่ใช่ด่านความปลอดภัย**
 * เซิร์ฟเวอร์ตรวจทั้งสิทธิ์และสถานะเองทุกคำขอ ที่ไม่แสดงเพราะไม่อยากให้ใครกด
 * สิ่งที่ยังไงก็ถูกปฏิเสธ จนเลิกอ่านข้อความผิดพลาดไปทั้งหมด
 */

import { useEffect, useRef, useState } from 'react';

import { ProductFailureNotice } from './ProductFailureNotice';
import { productStatusLabel } from './productView';
import { InfoHint } from '../components/InfoHint';
import {
  fetchProduct,
  transitionProduct,
  type ProductFailure,
} from '../../lib/products/client';
import {
  PRODUCT_MASTER_MODULE,
  PRODUCT_TRANSITIONS,
  PRODUCT_TRANSITION_RULES,
  type Product,
  type ProductTransition,
} from '../../lib/products/types';
import { can } from '../../lib/permissions/client';
import type { PermissionsState } from '../../lib/permissions/types';

export interface ProductTransitionPanelProps {
  /** record ที่กำลังถูกแสดงอยู่ — `status` ของก้อนนี้คือ `expected_status` ที่จะส่ง */
  readonly product: Product;
  readonly permissions: PermissionsState;
  readonly apiBaseUrl: string;
  readonly fetchImpl?: typeof fetch;
  /** ส่ง record ที่เซิร์ฟเวอร์ยืนยันแล้วกลับให้หน้ารายละเอียดแสดงแทนของเดิม */
  readonly onProductChanged: (product: Product) => void;
}

/** ผลของการดึงข้อมูลใหม่หลัง `409` — `null` คือยังไม่ได้ลอง (ความล้มเหลวแบบอื่น) */
type RefreshOutcome = 'ok' | 'failed';

type PanelState =
  | { readonly status: 'idle' }
  | { readonly status: 'working'; readonly transition: ProductTransition }
  | {
      readonly status: 'done';
      readonly transition: ProductTransition;
      readonly resultStatus: string;
    }
  | {
      readonly status: 'failed';
      readonly transition: ProductTransition;
      readonly failure: ProductFailure;
      readonly refreshed: RefreshOutcome | null;
    };

const ACTION_LABELS: Readonly<Record<ProductTransition, string>> = {
  submit: 'ส่งอนุมัติ',
  approve: 'อนุมัติ',
  reject: 'ไม่อนุมัติ',
};

const BUSY_LABELS: Readonly<Record<ProductTransition, string>> = {
  submit: 'กำลังส่งอนุมัติ…',
  approve: 'กำลังอนุมัติ…',
  reject: 'กำลังบันทึกการไม่อนุมัติ…',
};

const DONE_LABELS: Readonly<Record<ProductTransition, string>> = {
  submit: 'ส่งอนุมัติแล้ว',
  approve: 'อนุมัติแล้ว',
  reject: 'บันทึกการไม่อนุมัติแล้ว',
};

export function ProductTransitionPanel({
  product,
  permissions,
  apiBaseUrl,
  fetchImpl,
  onProductChanged,
}: ProductTransitionPanelProps) {
  const [state, setState] = useState<PanelState>({ status: 'idle' });

  // ผลของ request ที่กลับมาหลัง component ถูกถอดออกแล้วต้องไม่ถูกเขียนลง state
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  /**
   * คำสั่งที่ "ทำได้จริงตอนนี้" — ต้องตรงทั้งสถานะของ record และ flag ที่เซิร์ฟเวอร์
   * ส่งมา ปุ่มที่เห็นแล้วกดแล้วล้มเหลวเสมอ สอนให้คนเลิกอ่านข้อความผิดพลาด
   *
   * flag อ่านจาก `modules` ผ่าน `can()` เท่านั้น — ไม่ derive กติกา ADMIN/AUDITOR
   * ซ้ำจาก `system_roles` เพราะ `modules` คือผลลัพธ์หลัง guard ของเซิร์ฟเวอร์แล้ว
   */
  const offered = PRODUCT_TRANSITIONS.filter((transition) => {
    const rule = PRODUCT_TRANSITION_RULES[transition];
    return product.status === rule.source && can(permissions, PRODUCT_MASTER_MODULE, rule.action);
  });

  const run = async (transition: ProductTransition) => {
    if (state.status === 'working') {
      return;
    }

    // ⚠️ อ่านตรงนี้ ตอนกด และจากก้อนที่กำลังถูกแสดงอยู่
    const expectedStatus = product.status;
    setState({ status: 'working', transition });

    const result = await transitionProduct(product.product_id, transition, expectedStatus, {
      apiBaseUrl,
      fetchImpl,
    });
    if (!mounted.current) {
      return;
    }

    if (result.status === 'ready') {
      setState({ status: 'done', transition, resultStatus: result.product.status });
      onProductChanged(result.product);
      return;
    }

    // สถานะที่แสดงอยู่ไม่ถูกแตะ — ความล้มเหลวไม่ใช่การเปลี่ยนสถานะ
    setState({ status: 'failed', transition, failure: result, refreshed: null });
    if (result.status !== 'conflict') {
      return;
    }

    // `409` เท่านั้นที่ตามด้วยการอ่านใหม่ และเป็นการ **อ่าน** ไม่ใช่การสั่งซ้ำ
    const refreshed = await fetchProduct(product.product_id, { apiBaseUrl, fetchImpl });
    if (!mounted.current) {
      return;
    }
    if (refreshed.status === 'ready') {
      onProductChanged(refreshed.product);
      setState({ status: 'failed', transition, failure: result, refreshed: 'ok' });
      return;
    }
    setState({ status: 'failed', transition, failure: result, refreshed: 'failed' });
  };

  return (
    <>
      <section
        className="cap-card"
        aria-label="คำสั่งอนุมัติข้อมูลสินค้า"
        data-testid="product-transitions"
      >
        <div className="cap-card__head">
          <h2 className="cap-card__title">
            การอนุมัติ (D-010)
            <InfoHint
              label="เกี่ยวกับการแสดงปุ่มคำสั่งอนุมัติ"
              text="เซิร์ฟเวอร์เป็นผู้ตัดสินทุกคำขอ การแสดงหรือไม่แสดงปุ่มที่นี่เป็นเพียงความสะดวกของหน้าจอ"
            />
          </h2>
        </div>
        <div className="cap-card__foot">
          {offered.length === 0 ? (
            <p data-testid="product-transition-none">
              สถานะ “{productStatusLabel(product.status)}” กับสิทธิ์ชุดที่เซิร์ฟเวอร์ส่งมา
              ไม่มีคำสั่งให้สั่งจากหน้าจอนี้
            </p>
          ) : (
            <div className="cap-form__actions">
              {offered.map((transition) => (
                <button
                  key={transition}
                  type="button"
                  className={`cap-button ${
                    transition === 'reject' ? 'cap-button--ghost' : 'cap-button--primary'
                  }`}
                  data-testid={`product-transition-${transition}`}
                  disabled={state.status === 'working'}
                  onClick={() => {
                    void run(transition);
                  }}
                >
                  {state.status === 'working' && state.transition === transition
                    ? BUSY_LABELS[transition]
                    : ACTION_LABELS[transition]}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {state.status === 'done' ? (
        <div className="cap-banner" role="status" data-testid="product-transition-success">
          <strong>{DONE_LABELS[state.transition]}</strong>
          <span>
            สถานะล่าสุดที่เซิร์ฟเวอร์ยืนยันคือ {productStatusLabel(state.resultStatus)} (
            {state.resultStatus}) — ผู้ดำเนินการและเวลาที่บันทึกไว้อยู่ในหัวข้อ “ผู้ดำเนินการ” ด้านล่าง
          </span>
        </div>
      ) : null}

      {state.status === 'failed' ? (
        <>
          <ProductFailureNotice failure={state.failure} context="transition" />
          {state.refreshed === null ? null : (
            <p className="cap-banner__note" data-testid="product-transition-refresh">
              {state.refreshed === 'ok'
                ? 'ดึงข้อมูลล่าสุดของรายการนี้มาแสดงแทนแล้ว และไม่ได้ส่งคำสั่งเดิมซ้ำ — ตรวจสถานะจริงก่อนตัดสินใจใหม่'
                : 'ดึงข้อมูลล่าสุดไม่สำเร็จ สิ่งที่เห็นบนหน้าจออาจไม่ใช่สถานะจริงแล้ว — เปิดหน้านี้ใหม่ก่อนตัดสินใจ'}
            </p>
          )}
        </>
      ) : null}
    </>
  );
}
